import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Download, Eye, Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const QRCodesPage = () => {
  const { API, getAuthHeader, user } = useAuth();
  const [qrcodes, setQrcodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedQR, setSelectedQR] = useState(null);

  // Master data
  const [airports, setAirports] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);

  const [formData, setFormData] = useState({
    airport_code: "",
    location_code: "",
    location_description: "",
  });
  const [filterAirport, setFilterAirport] = useState("all");

  // Fetch master data
  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [airportsRes, locationsRes] = await Promise.all([
          fetch(`${API_BASE}/api/masters/airports`).then(r => r.json()),
          fetch(`${API_BASE}/api/masters/locations`).then(r => r.json()),
        ]);
        setAirports(airportsRes);
        setLocations(locationsRes);
      } catch (e) {
        console.error("Failed to fetch masters", e);
      }
    };
    fetchMasters();
  }, []);

  // Filter locations when airport changes in form
  useEffect(() => {
    if (formData.airport_code) {
      setFilteredLocations(locations.filter(l => l.airport_code === formData.airport_code));
    } else {
      setFilteredLocations([]);
    }
  }, [formData.airport_code, locations]);

  const fetchQRCodes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterAirport !== "all") params.append("airport_code", filterAirport);
      const response = await axios.get(`${API}/qrcodes?${params}`, getAuthHeader());
      setQrcodes(response.data);
    } catch (error) {
      console.error("Fetch QR codes error:", error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeader, filterAirport]);

  useEffect(() => { fetchQRCodes(); }, [fetchQRCodes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.airport_code || !formData.location_code) {
      toast.error("Please select airport and location");
      return;
    }
    // Find location details
    const loc = locations.find(l => l.location_code === formData.location_code);
    const airport = airports.find(a => a.airport_code === formData.airport_code);
    const payload = {
      airport_code: formData.airport_code,
      airport_name: airport?.airport_name || "",
      location_code: formData.location_code,
      terminal: `T${loc?.terminal_number || "1"}`,
      zone: loc?.terminal_type?.toLowerCase() || "",
      gate_type: loc?.gate_type || "",
      location: formData.location_description || `${loc?.terminal_type} ${loc?.gate_type}`,
      location_description: formData.location_description,
    };
    try {
      await axios.post(`${API}/qrcodes`, payload, getAuthHeader());
      toast.success("QR code created successfully");
      setDialogOpen(false);
      setFormData({ airport_code: "", location_code: "", location_description: "" });
      fetchQRCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create QR code");
    }
  };

  const handleDelete = async (qrId) => {
    if (!window.confirm("Delete this QR code?")) return;
    try {
      await axios.delete(`${API}/qrcodes/${qrId}`, getAuthHeader());
      toast.success("Deleted");
      fetchQRCodes();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const getQRUrl = (qr) => {
    const baseUrl = `https://wa.me/917550011029`;
    const msg = encodeURIComponent(`Hi! I'm at ${qr.airport_code || ""} ${qr.terminal || ""} ${qr.zone || ""} ${qr.gate_type || ""} - ${qr.location || ""}. QR: ${qr.code}`);
    return `${baseUrl}?text=${msg}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const downloadQR = (qr) => {
    const svg = document.getElementById(`qr-svg-${qr.id}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = 400; canvas.height = 450;
      ctx.fillStyle = "white"; ctx.fillRect(0, 0, 400, 450);
      ctx.drawImage(img, 50, 25, 300, 300);
      ctx.fillStyle = "#0A192F"; ctx.font = "bold 14px sans-serif"; ctx.textAlign = "center";
      ctx.fillText(qr.code, 200, 360);
      ctx.font = "12px sans-serif";
      ctx.fillText(`${qr.airport_code || ""} | ${qr.terminal || ""} | ${qr.zone || ""} | ${qr.location || ""}`, 200, 385);
      const link = document.createElement("a");
      link.download = `QR-${qr.code}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  const canManage = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="space-y-6" data-testid="qrcodes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-950" data-testid="qrcodes-title">QR Codes</h1>
          <p className="text-sm text-zinc-500 mt-1">Generate and manage location-specific QR codes from master data</p>
        </div>
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0A192F] hover:bg-zinc-800" data-testid="add-qrcode-button">
                <Plus className="w-4 h-4 mr-2" />Generate QR Code
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Generate New QR Code</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Airport</Label>
                  <Select value={formData.airport_code} onValueChange={(v) => setFormData({...formData, airport_code: v, location_code: ""})}>
                    <SelectTrigger data-testid="qr-airport-select"><SelectValue placeholder="Select Airport" /></SelectTrigger>
                    <SelectContent>
                      {airports.map(a => (
                        <SelectItem key={a.airport_code} value={a.airport_code}>{a.airport_code} - {a.city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Location</Label>
                  <Select value={formData.location_code} onValueChange={(v) => setFormData({...formData, location_code: v})}>
                    <SelectTrigger data-testid="qr-location-select"><SelectValue placeholder="Select Location" /></SelectTrigger>
                    <SelectContent>
                      {filteredLocations.map(l => (
                        <SelectItem key={l.location_code} value={l.location_code}>
                          {l.location_code} — T{l.terminal_number} {l.terminal_type} {l.gate_type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Description (optional)</Label>
                  <Input
                    value={formData.location_description}
                    onChange={(e) => setFormData({...formData, location_description: e.target.value})}
                    placeholder="e.g., Near Gate 5, Check-in Counter B"
                    data-testid="qr-description-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#0A192F] hover:bg-zinc-800" data-testid="qr-generate-button">Generate</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filter */}
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Airport</Label>
              <Select value={filterAirport} onValueChange={setFilterAirport}>
                <SelectTrigger className="w-48" data-testid="filter-airport"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Airports</SelectItem>
                  {airports.map(a => (
                    <SelectItem key={a.airport_code} value={a.airport_code}>{a.airport_code} - {a.city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-sm text-zinc-500">{qrcodes.length} QR codes</span>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead>Code</TableHead>
                <TableHead>Airport</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Gate</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Scans</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : qrcodes.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-zinc-500">No QR codes. Generate your first one.</TableCell></TableRow>
              ) : qrcodes.map(qr => (
                <TableRow key={qr.id}>
                  <TableCell>
                    <span className="font-mono font-bold text-sm">{qr.code}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{qr.airport_code || qr.terminal || "N/A"}</Badge>
                  </TableCell>
                  <TableCell>{qr.location_code || qr.zone || "N/A"}</TableCell>
                  <TableCell>{qr.gate_type || qr.location || "N/A"}</TableCell>
                  <TableCell className="text-xs text-zinc-500">{qr.location_description || qr.location || "-"}</TableCell>
                  <TableCell><Badge variant="secondary">{qr.scan_count || 0}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedQR(qr); setViewDialogOpen(true); }} data-testid={`view-qr-${qr.code}`}><Eye className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(getQRUrl(qr))}><Copy className="h-3.5 w-3.5" /></Button>
                      {canManage && <Button variant="ghost" size="sm" onClick={() => handleDelete(qr.id)} data-testid={`delete-qr-${qr.code}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View QR Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>QR Code: {selectedQR?.code}</DialogTitle></DialogHeader>
          {selectedQR && (
            <div className="flex flex-col items-center space-y-4 py-4">
              <QRCodeSVG id={`qr-svg-${selectedQR.id}`} value={getQRUrl(selectedQR)} size={200} />
              <div className="text-center text-sm space-y-1">
                <p className="font-bold">{selectedQR.airport_code} — {selectedQR.location_code || selectedQR.zone}</p>
                <p className="text-zinc-500">{selectedQR.gate_type || selectedQR.location}</p>
              </div>
              <Button size="sm" onClick={() => downloadQR(selectedQR)} className="bg-[#0A192F]"><Download className="h-4 w-4 mr-1" />Download PNG</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QRCodesPage;
